import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Tag, Typography } from 'antd';

import { errorMessage } from '../../MarketExplorer/api';
import { rejectProposal } from './api';
import { REJECT_REASON_LABELS } from './format';
import type { RejectTarget } from './PreviewDrawer';
import { Proposal, REJECT_REASONS, RejectReason } from './types';

type Props = {
  target: RejectTarget | null;
  onClose: () => void;
  onRejected: (proposal: Proposal) => void;
};

/**
 * Reject with at least one reason chip and an optional note. The builder
 * remembers the reasons, so a later search avoids the same ground.
 */
const RejectModal: React.FC<Props> = ({ target, onClose, onRejected }) => {
  const [reasons, setReasons] = useState<RejectReason[]>([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setReasons([]);
    setNote('');
    setError('');
    setSubmitting(false);
  }, [target]);

  const confirm = async () => {
    if (!target || !reasons.length) return;
    setSubmitting(true);
    setError('');
    try {
      const proposal = await rejectProposal(target.proposalId, {
        reasons,
        note,
      });
      onRejected(proposal);
    } catch (reason) {
      setError(errorMessage(reason) || 'The proposal could not be rejected.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={target != null}
      title={target ? `Reject "${target.title}"` : ''}
      onCancel={onClose}
      onOk={() => void confirm()}
      okText="Reject"
      okButtonProps={{ danger: true, disabled: !reasons.length }}
      confirmLoading={submitting}
      destroyOnHidden
    >
      <div className="blueprint-builder__modal">
        <Typography.Text>Why? Pick at least one.</Typography.Text>
        <Tag.CheckableTagGroup<RejectReason>
          multiple
          aria-label="Reasons"
          className="blueprint-builder__reasons"
          value={reasons}
          onChange={value => setReasons(value || [])}
          options={REJECT_REASONS.map(value => ({
            value,
            label: REJECT_REASON_LABELS[value],
          }))}
        />
        <label className="blueprint-builder__field">
          <span>Note (optional)</span>
          <Input.TextArea
            aria-label="Note"
            value={note}
            autoSize={{ minRows: 2, maxRows: 6 }}
            onChange={event => setNote(event.target.value)}
          />
        </label>
        {error && <Alert type="error" showIcon title={error} />}
      </div>
    </Modal>
  );
};

export default RejectModal;
